import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Info, CalendarDays, Loader2, Moon } from "lucide-react";
import { sensors } from "../mock"; // solo metadati (no dati)
import { useAuth } from "../context/AuthContext";
import {
  SENSOR_TO_BACKEND,
  pointsToChartSeries,
  statsToSeries,
} from "../lib/sensorApi";
import LineChart from "../components/LineChart";
import SensorIcon from "../components/SensorIcon";

const ranges = [
  { key: "today", label: "Oggi" },
  { key: "7d", label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
];

export default function SensorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [range, setRange] = useState("today");
  const [showInfo, setShowInfo] = useState(false);

  const sensor = sensors.find((s) => s.id === id) || sensors[0];
  const backendKey = SENSOR_TO_BACKEND[sensor.id];

  const [data, setData] = useState(null); // array {value, time/label} oppure null=loading
  const [aggs, setAggs] = useState(null); // {min, max, avg, current} dal /today
  const [err, setErr] = useState("");

  // Carica serie e aggregati. Reset quando cambi range o sensore.
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setAggs(null);
    setErr("");
    (async () => {
      try {
        if (range === "today") {
          // Per "oggi" servono due chiamate: serie + aggregati
          const [seriesRes, todayRes] = await Promise.all([
            api.get(
              `/api/user/me/today/series?sensor=${backendKey}&limit=2000`
            ),
            api.get("/api/user/me/today"),
          ]);
          if (cancelled) return;
          const points = seriesRes.data?.points || [];
          setData(seriesRes.data?.has_data ? pointsToChartSeries(points) : []);
          const tagg = todayRes.data?.sensors?.[backendKey];
          if (tagg) {
            setAggs({
              min: tagg.min,
              max: tagg.max,
              avg: tagg.avg,
              current: tagg.current,
            });
          }
        } else {
          // 7d / 30d → /stats con range corrispondente
          const r = await api.get(`/api/user/me/stats?range=${range}`);
          if (cancelled) return;
          const series = statsToSeries(r.data, sensor.id);
          setData(series);
          // Calcola aggregati lato client dai valori giornalieri
          if (series.length) {
            const vals = series.map((p) => p.value).filter((v) => v != null);
            setAggs({
              min: Math.min(...vals),
              max: Math.max(...vals),
              avg: Number(
                (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1)
              ),
              current: vals[vals.length - 1],
            });
          }
        }
      } catch (e) {
        if (!cancelled) setErr("Impossibile caricare i dati");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, range, sensor.id, backendKey]);

  const otherSensors = sensors.filter((s) => s.id !== sensor.id);
  const isLoading = data === null && !err;
  const isEmpty = !isLoading && !err && Array.isArray(data) && data.length === 0;

  return (
    <div className="px-4 pt-3 pb-36">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors"
          aria-label="indietro"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-white text-lg font-semibold uppercase tracking-wider text-center flex-1 px-2">
          {sensor.subtitle}
        </h1>
        <button
          onClick={() => setShowInfo((v) => !v)}
          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors"
          aria-label="info"
        >
          <Info size={18} />
        </button>
      </div>

      {showInfo && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3 text-white/85 text-[13px] leading-relaxed">
          {sensor.info}
        </div>
      )}

      {/* Range selector */}
      <div className="mt-5 flex items-center gap-2">
        {ranges.map((r) => {
          const active = range === r.key;
          return (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all border ${
                active
                  ? "bg-[#00a88a] text-white border-[#00a88a] shadow-[0_8px_20px_-10px_rgba(0,168,138,0.7)]"
                  : "text-white/85 border-white/15 bg-white/5 hover:bg-white/10"
              }`}
            >
              {r.label}
            </button>
          );
        })}
        <button
          className="w-10 h-10 rounded-xl bg-[#00a88a] text-white flex items-center justify-center shadow-[0_8px_20px_-10px_rgba(0,168,138,0.7)] hover:brightness-110"
          aria-label="calendario"
        >
          <CalendarDays size={18} />
        </button>
      </div>

      {/* Stat row */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { label: "Min", value: aggs?.min },
          { label: "Media", value: aggs?.avg },
          { label: "Max", value: aggs?.max },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-2xl bg-white/5 border border-white/10 px-3 py-2 text-center"
          >
            <div className="text-white/60 text-[11px] uppercase tracking-wider">
              {s.label}
            </div>
            <div className="text-white text-lg font-semibold">
              {s.value ?? "—"}
              <span className="text-[11px] opacity-70 ml-1">{sensor.title}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Chart / stati */}
      <div className="mt-4">
        {isLoading && (
          <div
            className="rounded-[22px] border border-white/10 bg-white/5 h-[260px] flex items-center justify-center text-white/70 text-sm gap-2"
            data-testid="sensor-loading"
          >
            <Loader2 size={18} className="animate-spin" />
            Caricamento dati…
          </div>
        )}

        {err && !isLoading && (
          <div className="rounded-[22px] border border-rose-400/30 bg-rose-500/10 p-4 text-rose-100 text-sm">
            {err}
          </div>
        )}

        {isEmpty && (
          <div
            className="rounded-[22px] border border-white/10 bg-white/[0.04] h-[260px] flex flex-col items-center justify-center text-center px-6"
            data-testid="sensor-empty"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#14c2a1]/15 border border-[#14c2a1]/25 flex items-center justify-center mb-3">
              <Moon size={20} className="text-[#67e8d1]" />
            </div>
            <div className="text-white text-[14px] font-semibold">
              In attesa dei dati del sensore
            </div>
            <p className="mt-1 text-white/65 text-[12.5px] max-w-[260px] leading-snug">
              {range === "today"
                ? "Avvia una sessione di sonno per popolare questo grafico."
                : "Servono almeno alcuni giorni di sessioni complete per popolare questo intervallo."}
            </p>
          </div>
        )}

        {!isLoading && !err && data && data.length > 0 && (
          <LineChart
            data={data}
            yMin={sensor.yRange[0]}
            yMax={sensor.yRange[1]}
            unit={sensor.title}
            height={260}
          />
        )}
      </div>

      {/* Current value */}
      <div className="mt-5 flex items-end justify-between">
        <div>
          <div className="text-white/60 text-xs uppercase tracking-wider">
            Valore attuale
          </div>
          <div className="text-white text-4xl font-semibold tracking-tight leading-none mt-1">
            {aggs?.current ?? "—"}
            <span className="text-sm ml-1 opacity-70">{sensor.title}</span>
          </div>
        </div>
      </div>

      {/* Footer promo */}
      <div className="mt-8">
        <h3 className="text-white text-xl font-semibold tracking-tight leading-tight">
          Monitora la tua salute con l'app SmartDream
        </h3>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {otherSensors.slice(0, 3).map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/sensor/${o.id}`)}
              className="rounded-2xl p-3 flex flex-col items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(155deg, #10a78c 0%, #0a8672 100%)",
                boxShadow: "0 8px 22px -14px rgba(0,168,138,0.55)",
              }}
            >
              <SensorIcon name={o.icon} size={26} className="text-white" />
              <span className="text-white text-[11px] leading-tight text-center">
                {o.subtitle}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {otherSensors.slice(3).map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/sensor/${o.id}`)}
              className="rounded-2xl p-3 flex flex-col items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(155deg, #10a78c 0%, #0a8672 100%)",
                boxShadow: "0 8px 22px -14px rgba(0,168,138,0.55)",
              }}
            >
              <SensorIcon name={o.icon} size={26} className="text-white" />
              <span className="text-white text-[11px] leading-tight text-center">
                {o.subtitle}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
